import React, { type FC } from 'react';
import { defineMessages, FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import type { NewCoreIconProps } from '@atlaskit/icon';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import LockIcon from '@atlaskit/icon/core/lock-locked';
import UnlockIcon from '@atlaskit/icon/core/lock-unlocked';
import GlobeIcon from '@atlaskit/icon/core/globe';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { ConfluenceLogoIcon } from '@confluence/shareable-links/entry-points/ConfluenceLogoIcon.ts';

export const i18n = defineMessages({
	share: {
		id: 'share-and-restrict-dialog.controlled-share-button.share',
		defaultMessage: 'Share',
		description:
			'Button text for the Share and Restrict button, which opens the Share and Restrict dialog',
	},
	shareArialLabel: {
		id: 'share-and-restrict-dialog.controlled-share-button.share-aria-label',
		defaultMessage: 'Share, {tooltipMessage}',
		description: 'ARIA label for the Share and Restrict button',
	},
});

export type ControlledShareButtonProps = {
	icon: ControlledShareButtonIcon;
	onClick: () => void;
	tooltipMessage: MessageDescriptor;
	isDisabled: boolean;
	children?: React.ReactNode;
	focusRef?: React.RefObject<HTMLButtonElement>;
};

export type ControlledShareButtonIcon = 'open' | 'restricted' | 'public' | 'error' | 'internal';

// Adapts ConfluenceLogoIcon (SVG) to the Atlaskit icon interface expected by Button iconBefore.
const ConfluenceLogoIconWrapper: FC<NewCoreIconProps> = ({ color }) => (
	<ConfluenceLogoIcon color={color} />
);

const ICON_PROP_TO_ICON_COMPONENT: {
	[key in ControlledShareButtonIcon]:
		| typeof UnlockIcon
		| typeof LockIcon
		| typeof GlobeIcon
		| typeof StatusErrorIcon
		| typeof ConfluenceLogoIconWrapper;
} = {
	open: UnlockIcon,
	restricted: LockIcon,
	public: GlobeIcon,
	error: StatusErrorIcon,
	internal: ConfluenceLogoIconWrapper,
};

const makeIconProps = (
	icon: ControlledShareButtonIcon,
	isDisabled: boolean,
): Partial<NewCoreIconProps> => {
	if (isDisabled) {
		return { color: token('color.icon.disabled') };
	}
	switch (icon) {
		case 'error':
			return { color: token('color.icon.danger') };
		case 'restricted':
			return { color: token('color.icon.danger') };
		default:
			return { color: token('color.icon.subtle') };
	}
};

export const ControlledShareButton: FC<ControlledShareButtonProps> = ({
	icon,
	onClick,
	tooltipMessage,
	isDisabled,
	children,
	focusRef,
}) => {
	const { formatMessage } = useIntl();
	const Icon = ICON_PROP_TO_ICON_COMPONENT[icon];
	const iconProps: NewCoreIconProps = {
		label: '',
		...makeIconProps(icon, isDisabled),
	};

	return (
		<Tooltip
			content={formatMessage(tooltipMessage)}
			position="bottom"
			canAppear={() => !children}
			hideTooltipOnMouseDown
			ignoreTooltipPointerEvents
			delay={100}
		>
			<Button
				appearance="subtle"
				iconBefore={(parentProps) => <Icon {...parentProps} {...iconProps} size="medium" />}
				onClick={onClick}
				aria-label={formatMessage(i18n.shareArialLabel, {
					tooltipMessage: formatMessage(tooltipMessage),
				})}
				isDisabled={isDisabled}
				ref={focusRef}
				testId="controlled-share-button"
			>
				{children}
				<FormattedMessage {...i18n.share} />
			</Button>
		</Tooltip>
	);
};
